//react
import React, { useReducer, useEffect, useMemo } from "react";
import { Grid, Box, Divider, Typography } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import TextField from "@mui/material/TextField";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import dayjs from "dayjs";
import { isValid } from "date-fns";
import { capitalize, get } from "lodash";
import "./deduction.css";
import Deductions from "./Deductions/Deductions";
import { PAGINATION } from "utils/constants/pagination";
import { useDispatch, useSelector } from "store";
import {
  fetchDeductionData,
  fetchViewDeductionData,
  resetDeductions,
  reverseDeduction,
  runDeduction,
} from "store/reducers/deductions";
import MainCard from "components/MainCard";
import ScrollX from "components/ScrollX";
import {
  convertDateIntoFrontEndFormat,
  dateFormat,
  getBEDateFormat,
} from "utils/date";
import CashDAutocomplete from "components/common/CashDAutocomplete";
import T from "utils/constants/T";
import RunDeduction from "./Deductions/DeductionActions/RunDeduction";
import ReverseDeduction from "./Deductions/DeductionActions/ReverseDeduction";
import {
  fetchDeductionTransactionBusinessUnitList,
  fetchDeductionTransactionPayPeriodList,
  fetchDeductionTransactionSchedulesList,
} from "store/reducers/deductionTransactionFilter";
import { DEDUCTION_STATUS_LIST } from "utils/constants/deductions";
import toast from "utils/ToastNotistack";
import { useNavigate } from "react-router";
import authApi from "interceptor/authInterceptor";
import Loader from "components/Loader";
import { WEBHOOK_ENABLED_SYSTEMS } from "utils/constants/common";
import { fetchCompanyDetails } from "store/reducers/companyTabDetails";
import { INCOMPLETE_WITHDRAWAL_STATUSES } from "utils/constants/transactions";

const { INITIAL_PAGE, ROWS_PER_PAGE } = PAGINATION;
const EXPORT_PAGE_SIZE = 10000;

const DeductionPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { selectedCompany } = useSelector(
    (state: any) => state.dashboardSearch
  );
  const selectedCompanyName = get(selectedCompany, "company_name", "");
  const selectedCompanyId = get(selectedCompany, "_id", "");

  const {
    deductionTableList,
    deductionViewList,
    totalDeductionItems,
    totalDeductionPages,
    pageNo,
    pageSize,
    loader,
  } = useSelector((state: any) => state.deductionData);

  const { scheduleList, businessUnitList } = useSelector(
    (state: any) => state.deductionTransactionFilter
  );
  const { systemPayrollCode , currencySymbol, webhookEnabled } = useSelector(
    (state: any) => state.companyTabData
  );
  let cs = currencySymbol ?  currencySymbol : T.DOLLAR;

  const [localState, setLocalState] = useReducer(
    (prevState: any, newState: any) => ({ ...prevState, ...newState }),
    {
      page: pageNo,
      rowsPerPage: pageSize,
      schedule: null,
      deductionStatus: null,
      businessUnit: null,
      fromDate: null,
      toDate: null,
      openViewDeductionDialog: false,
      openRunDeductionDialog: false,
      openReverseDeductionDialog: false,
      payPeriod: null,
      companyId: "",
      viewPayPeriodId: "",
      deductionRecord: {},
    }
  );

  const {
    page,
    rowsPerPage,
    schedule,
    deductionStatus,
    businessUnit,
    fromDate,
    toDate,
    openViewDeductionDialog,
    openRunDeductionDialog,
    openReverseDeductionDialog,
    payPeriod,
    companyId,
    viewPayPeriodId,
    deductionRecord,
  } = localState;

  //filters arrays
  const payPeriodId = payPeriod ? get(payPeriod, "_id", "") : null;
  const scheduleId = useMemo(() => {
    if (!schedule) return null;
    const isWebhookEnabled = webhookEnabled && WEBHOOK_ENABLED_SYSTEMS.includes(systemPayrollCode);
    const isSupportedPayrollSystem = ["TIMESITE_PRO"].includes(systemPayrollCode);
    const key = (isSupportedPayrollSystem || isWebhookEnabled) ? "cycle_id" : "_id";
    console.log("Key", key, schedule)
    return get(schedule, key, "");
  }, [schedule, systemPayrollCode, webhookEnabled]);

  const businessUnitId = businessUnit ? get(businessUnit, "_id", "") : null;

  useEffect(() => {
    dispatch(fetchCompanyDetails(selectedCompanyId));
  }, [selectedCompanyId]);

  //filter listing arrays initial rendering
  useEffect(() => {
    if (selectedCompanyId) {
      dispatch(fetchDeductionTransactionPayPeriodList(selectedCompanyId));
      dispatch(fetchDeductionTransactionSchedulesList(selectedCompanyId));
      if (selectedCompanyName.includes(capitalize(T.HUDSON))) {
        dispatch(fetchDeductionTransactionBusinessUnitList(selectedCompanyId));
      }
    }
  }, [selectedCompanyId]);

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (
    event: React.MouseEvent<HTMLButtonElement>,
    companyId: string,
    payPeriodId: string,
    deductionRecord: any
  ) => {
    setAnchorEl(event.currentTarget);
    setLocalState({
      companyId: companyId,
      viewPayPeriodId: payPeriodId,
      deductionRecord,
    });
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  //View Deduction Modal opening handler
  const handleViewDeductionDailog = () => {
    setLocalState({ openViewDeductionDialog: !openViewDeductionDialog });
  };

  //Deduction run handler
  const handleRunDeductionDailog = () => {
    setLocalState({ openRunDeductionDialog: !openRunDeductionDialog });
  };

  //Reverse reduction handler
  const handleReverseDeductionDailog = () => {
    setLocalState({ openReverseDeductionDialog: !openReverseDeductionDialog });
  };

  //fetch view deduction table Data
  const handleViewDeduction = () => {
    dispatch(
      fetchViewDeductionData(
        INITIAL_PAGE,
        ROWS_PER_PAGE,
        companyId,
        viewPayPeriodId,
        deductionStatus
      )
    );
    setLocalState({ viewPayPeriodId: viewPayPeriodId, companyId: companyId });
    handleViewDeductionDailog();
    handleClose();
  };
  let viewDeductionRows: any = [];
  deductionViewList.map((item: any, index: number) => {
    const totalDeductionUnsent = Number(get(item, "total_deductions_unsent", 0));
    const totalFeeUnsent =  Number(get(item, "total_fee_unsent", 0));
    const totalDeductionSent = Number(get(item, "total_deductions_sent", 0));
    const totalFeeSent = Number(get(item, "total_fee_sent", 0))
    const totalCapitalSent = totalDeductionSent - totalFeeSent;
    const totalCapitalUnsent = totalDeductionUnsent - totalFeeUnsent;
    viewDeductionRows[index] = {
      srNo: index + 1,
      employeeName: get(item, "staff.fullname", ""),
      employeeType: get(item, "staff.fullname", ""),
      totalAmountSent: totalDeductionSent,
      totalAmountUnsent: totalDeductionUnsent,
      totalCapitalSent,
      totalCapitalUnsent,
      totalFeeSent,
      totalFeeUnsent,
    };
  });

  const handleExportViewTransaction = () => {
    const filteredList = deductionTableList.filter(
      (item: any) => get(item, "_id", "") === viewPayPeriodId
    );
    authApi
      .get(
        `/v2/api/timesheets/getAllDeductions?page=0&pageSize=50000&company_id=${companyId}&pay_period_id=${viewPayPeriodId}&schedule_id=&business_unit_id=&startDate=`
      )
      .then(async (res) => {
        console.log(filteredList[0]);
        const finalData = res.data.data;
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet("Transactions");
        const formattedDateName = dayjs().format("DDMMMYYYY");

        // Crie o nome do arquivo com o formato desejado
        const fileName =
          `${filteredList[0].company_details.company_name}_Transaction_${formattedDateName}.xlsx`.replace(
            / /g,
            "_"
          );
        worksheet.mergeCells("A1:I1");
        const titleCell = worksheet.getCell("A1");
        titleCell.value = "Withdrawals Reports";
        titleCell.alignment = { horizontal: "center", vertical: "middle" };
        titleCell.font = { bold: true, size: 16, color: { argb: "0073e6" } };

        const companyName = selectedCompanyName;
        const startDate = dayjs(filteredList[0].start_date).format("D/M");
        const endDate = dayjs(filteredList[0].end_date).format("D/M");
        let payPeriod = `${startDate} to ${endDate}`;
        if (filteredList[0].company_details.system_code.code == "ASTUTE") {
          payPeriod = `${filteredList[0].xero_pay_calendar?.Name} ${startDate} to ${endDate}`;
        } else if (
          ["DEPUTY", "TIMESITE_PRO"].includes(
            filteredList[0].company_details.system_code.code
          )
        ) {
          payPeriod = `${filteredList[0].pay_period_origination?.name} ${startDate} to ${endDate}`;
        } else if (
          filteredList[0].company_details.system_code.code == "KEYPAY"
        ) {
          payPeriod = `${filteredList[0].keypay_pay_schedule?.name} ${startDate} to ${endDate}`;
        } else if (
          WEBHOOK_ENABLED_SYSTEMS.includes(
            filteredList[0]?.company_details?.system_code.code
          ) &&
          !!filteredList[0]?.company_details?.webhook_enabled
        ) {
          payPeriod = `${filteredList[0].pay_period_origination?.name} ${startDate} to ${endDate}`;
        }
        const dateReport = dayjs().format("DD-MM-YYYY");

        worksheet.mergeCells("A2:C2");
        worksheet.getCell("A2").value = `Company: ${companyName}`;
        worksheet.getCell("A2").alignment = {
          horizontal: "left",
          vertical: "middle",
        };

        worksheet.mergeCells("A3:C3");
        worksheet.getCell("A3").value = `Pay Period: ${payPeriod}`;
        worksheet.getCell("A3").alignment = {
          horizontal: "left",
          vertical: "middle",
        };

        worksheet.mergeCells("A4:C4");
        worksheet.getCell("A4").value = `Date Report: ${dateReport}`;
        worksheet.getCell("A4").alignment = {
          horizontal: "left",
          vertical: "middle",
        };

        worksheet.addRow([]);

        let runningTotal = 0;
        let feeTotal = 0;
        finalData.forEach((row: any) => {
          runningTotal += parseFloat(get(row, "total_deduction", 0));
          feeTotal += parseFloat(get(row, "fee_amount", 0));
        });

        worksheet.getCell('G2').value = `Running Total: ${cs}${runningTotal.toFixed(2)}`;
        worksheet.getCell('G2').alignment = { horizontal: 'left', vertical: 'middle' };

        worksheet.getCell('G3').value = `Fee Total: ${cs}${feeTotal.toFixed(2)}`;
        worksheet.getCell('G3').alignment = { horizontal: 'left', vertical: 'middle' };

        const headers = [
          "#",
          "Company Name",
          "Staff Name",
          "Date",
          "Description",
          "Type",
          "Debit",
          "Credit",
          "Balance",
        ];
        worksheet.getRow(5).values = headers;

        worksheet.getRow(5).eachCell((cell) => {
          cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
          cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "0073e6" },
          };
          cell.alignment = { vertical: "middle", horizontal: "center" };
        });

        finalData.forEach((row: any, index: number) => {
          const formattedDate = dayjs(get(row, "created_date")).format(
            "DD-MM-YYYY h:mm:ss A"
          );
          const newRow = worksheet.addRow([
            index + 1,
            selectedCompanyName,
            get(row, "staff.fullname", ""),
            formattedDate,
            'Withdrawal to employee ' + get(row, 'staff.fullname', ''),
            'DD',
            `${cs}` + get(row, 'total_deduction', 0).toFixed(2) + ` (Inc. ${cs} ` + get(row, 'fee_amount', 0) + ' Fee)',
            get(row, 'credit', 0),
            `( ${cs}` + get(row, 'total_deduction', 0).toFixed(2) + ')'
          ]);
          const balanceCell = newRow.getCell(9);
          balanceCell.font = { color: { argb: "FFFF0000" } };
        });

        worksheet.getColumn(6).numFmt = "$#,##0.00";
        worksheet.getColumn(7).numFmt = "$#,##0.00";

        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        saveAs(blob, fileName);
        setLocalState({
          viewPayPeriodId: viewPayPeriodId,
          companyId: companyId,
        });

        handleClose();
      })
      .catch((error) => {
        toast(get(error, "message", ""), { variant: "error" });
      });
  };
  const handleDeputyExportation = async (finalData: any, filteredList: any) => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Summary");
    const startDate = dayjs(filteredList[0].start_date).format("D/M");
    const endDate = dayjs(filteredList[0].end_date).format("D/M");
    const formattedDateName = dayjs().format("DDMMMYYYY");
    const fileName =
      `${filteredList[0].company_details.company_name}_Summary_${formattedDateName}.xlsx`.replace(
        / /g,
        "_"
      );
    let totalDeductionSentAmount = 0;
    let totalDeductionUnsentAmount = 0;
    let total_amount_sent = 0;
    let total_amount_unsent = 0;
    finalData.forEach((row: any) => {
      totalDeductionSentAmount += parseFloat(get(row, "total_deductions_sent", 0));
      totalDeductionUnsentAmount += parseFloat(get(row, "total_deductions_unsent", 0));
      total_amount_sent = totalDeductionSentAmount;
      total_amount_unsent = totalDeductionUnsentAmount;
    });
    worksheet.mergeCells("A1:I1");
    const titleCell = worksheet.getCell("A1");
    titleCell.value = "Summary Reports";
    titleCell.alignment = { horizontal: "center", vertical: "middle" };
    titleCell.font = { bold: true, size: 16, color: { argb: "0073e6" } };

    const companyName = selectedCompanyName;

    const payPeriod = `${startDate} to ${endDate}`;
    const dateReport = dayjs().format("DD-MM-YYYY");

    worksheet.mergeCells("A2:C2");
    worksheet.getCell("A2").value = `Company: ${companyName}`;
    worksheet.getCell("A2").alignment = {
      horizontal: "left",
      vertical: "middle",
    };

    worksheet.mergeCells("A3:C3");
    worksheet.getCell("A3").value = `Pay Period: ${payPeriod}`;
    worksheet.getCell("A3").alignment = {
      horizontal: "left",
      vertical: "middle",
    };

    worksheet.mergeCells("A4:C4");
    worksheet.getCell("A4").value = `Date Report: ${dateReport}`;
    worksheet.getCell("A4").alignment = {
      horizontal: "left",
      vertical: "middle",
    };
    worksheet.getCell("G2").value = `Schedule Name: `;
    worksheet.getCell("G2").alignment = {
      horizontal: "left",
      vertical: "middle",
    };

    worksheet.mergeCells('G2:I2');
    worksheet.getCell('G3').value = `Sent Amount: ${cs}${total_amount_sent.toFixed(2)}`;
    worksheet.getCell('G3').alignment = { horizontal: 'left', vertical: 'middle' };

    worksheet.mergeCells('G3:I3');
    worksheet.getCell('G4').value = `Unsent Amount: ${cs}${total_amount_unsent.toFixed(2)}`;
    worksheet.getCell('G4').alignment = { horizontal: 'left', vertical: 'middle' };
    worksheet.mergeCells('G4:I4');

    worksheet.addRow([]);
    const aggregatedData = finalData.reduce(
      (
        acc: {
          [x: string]: {
            [x: string]: number;
            totalFeeSent: number;
          };
        },
        row: any
      ) => {
        const fullname = get(row, "staff.fullname", "Unknown");
        if (!acc[fullname]) {
          acc[fullname] = {
            fullname: fullname,
            totalAmountSent: 0,
            totalFeeSent: 0,
            totalAmountUnSent: 0,
            totalFeeUnSent: 0,
          };
        }
        acc[fullname].totalAmountSent += parseFloat(
          get(row, "total_amount_sent", 0)
        );
        acc[fullname].totalFeeSent += parseFloat(get(row, "total_fee_sent", 0));
        acc[fullname].totalAmountUnSent += parseFloat(
          get(row, "total_amount_unsent", 0)
        );
        acc[fullname].totalFeeUnSent += parseFloat(
          get(row, "total_fee_unsent", 0)
        );
        return acc;
      },
      {}
    );
    const groupedData = Object.values(aggregatedData);

    const headers1 = [
      "#",
      "Employees Name",
      "Employees Type",
      "Sent Amount",
      "",
      "",
      "Unsent Amount",
      "",
      "",
    ];
    worksheet.getRow(5).values = headers1;
    worksheet.mergeCells("D5:F5");
    worksheet.mergeCells("G5:I5");

    const headers2 = [
      "",
      "",
      "",
      "Capital",
      "Fee",
      "Total",
      "Capital",
      "Fee",
      "Total",
    ];
    worksheet.getRow(6).values = headers2;

    worksheet.getRow(5).eachCell((cell) => {
      cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "0073e6" },
      };
      cell.alignment = { vertical: "middle", horizontal: "center" };
    });
    worksheet.getRow(6).eachCell((cell) => {
      cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "0073e6" },
      };
      cell.alignment = { vertical: "middle", horizontal: "center" };
    });

    groupedData.forEach((row: any, index) => {
      const newRow = worksheet.addRow([
        index + 1,
        row.fullname,
        "Timesheet",
        `${cs}` + row.totalAmountSent.toFixed(2),
        `${cs}` + row.totalFeeSent.toFixed(2),
        `${cs}` + (row.totalAmountSent + row.totalFeeSent).toFixed(2),
        `${cs}` + row.totalAmountUnSent.toFixed(2),
        `${cs}` + row.totalFeeUnSent.toFixed(2),
        `${cs}` + (row.totalAmountUnSent + row.totalFeeUnSent).toFixed(2)
      ]);

      [newRow.getCell(4), newRow.getCell(5), newRow.getCell(6)].forEach(
        (cell) => {
          cell.font = { color: { argb: "32ff32" } };
        }
      );
      [newRow.getCell(7), newRow.getCell(8), newRow.getCell(9)].forEach(
        (cell) => {
          cell.font = { color: { argb: "ff0000" } };
        }
      );
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    saveAs(blob, fileName);
  };
  const handleMyobExportation = async (finalData: any, filteredList: any) => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Summary");
    const formattedDateName = dayjs().format("DDMMMYYYY");
    const fileName =
      `${filteredList[0].company_details.company_name}_Summary_${formattedDateName}.xlsx`.replace(
        / /g,
        "_"
      );
    const aggregatedData = finalData.reduce(
      (
        acc: {
          [fullname: string]: {
            fullname: string;
            payroll_id: string;
            schedule_name: string;
            totalAmountSent: number;
            totalFeeSent: number;
            totalAmountUnSent: number;
            totalFeeUnSent: number;
          };
        },
        row: any
      ) => {
        const fullname = get(row, "staff.fullname", "Unknown");
        const payroll_id = get(row, "staff.system_employee_id", "");

        if (!acc[fullname]) {
          acc[fullname] = {
            fullname: fullname,
            payroll_id: payroll_id,
            schedule_name: filteredList[0].schedule_name,
            totalAmountSent: 0,
            totalFeeSent: 0,
            totalAmountUnSent: 0,
            totalFeeUnSent: 0,
          };
        }

        acc[fullname].totalAmountSent += parseFloat(
          get(row, "total_deductions_sent", 0)
        );
        acc[fullname].totalFeeSent += parseFloat(get(row, "total_fee_sent", 0));
        acc[fullname].totalAmountUnSent += parseFloat(
          get(row, "total_amount_unsent", 0)
        );
        acc[fullname].totalFeeUnSent += parseFloat(
          get(row, "total_fee_unsent", 0)
        );

        return acc;
      },
      {}
    );

    const groupedData = Object.values(aggregatedData);

    const headers1 = [
      "Ext. employee ID",
      "Ext. employee name",
      "Ext. pay item ID",
      "Ext. pay item description",
      "Timesheet date",
      "Amount",
    ];
    worksheet.getRow(1).values = headers1;
    worksheet.getRow(1).eachCell((cell) => {
      cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "7030a0" },
      };
      cell.alignment = { vertical: "middle", horizontal: "center" };
    });

    groupedData.forEach((row: any, index) => {
      worksheet.addRow([
        row.payroll_id,
        row.fullname,
        '',
        'CashD RTR',
        '',
        (row.totalAmountSent > 0 ? (row.totalAmountSent).toFixed(2) : (row.totalAmountUnSent + row.totalFeeUnSent).toFixed(2)),
      ]);
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    saveAs(blob, fileName);
  };
  const handleAstuteExportation = async (
    finalData: any,
    filteredList: any,
    transactions: any
  ) => {
    const ExcelJS = require("exceljs");
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Summary");
    const formattedDate = dayjs().format("D/M/YYYY");
    const startDate = dayjs(filteredList[0].start_date).format("D/M");
    const endDate = dayjs(filteredList[0].end_date).format("D/M");
    const formattedDateNameFileName = dayjs().format("DDMMMYYYY");
    const fileName =
      `${filteredList[0].company_details.company_name}_Summary_${formattedDateNameFileName}.xlsx`.replace(
        / /g,
        "_"
      );

    const transactionsByName = transactions.reduce(
      (
        acc: any,
        item: {
          staff: { fullname: string };
          pay_deductions: { status: string };
        }
      ) => {
        const fullName = item.staff.fullname;
        const status = item.pay_deductions.status;
        if (!INCOMPLETE_WITHDRAWAL_STATUSES.includes(status)) {
          acc[fullName] = (acc[fullName] ?? 0) + 1;
        }

        return acc;
      },
      {}
    );

    const aggregatedData = finalData.reduce(
      (
        acc: {
          [fullname: string]: {
            first_name: string;
            last_name: string;
            business_unit_name: string;
            payroll_id: string;
            employee_id: string;
            pay_cycle_period: string;
            schedule_name: string;
            totalAmountSent: number;
            totalFeeSent: number;
            totalAmountUnSent: number;
            totalFeeUnSent: number;
            txn_count: number;
          };
        },
        row: any
      ) => {
        const fullname = get(row, "staff.fullname", "Unknown");
        const first_name = get(row, "staff.first_name", "Unknown");
        const last_name = get(row, "staff.last_name", "Unknown");
        const business_unit_name = get(row, "business_unit_name", "Unknown");
        const employee_id = get(row, "staff._id", "Unknown");
        const payroll_id = get(row, "staff.system_employee_id", "");

        if (!acc[fullname]) {
          acc[fullname] = {
            first_name: first_name,
            last_name: last_name,
            business_unit_name: business_unit_name,
            payroll_id: payroll_id,
            employee_id: employee_id,
            pay_cycle_period:
              dayjs(filteredList[0].start_date).format("D/M/YYYY") +
              "-" +
              dayjs(filteredList[0].end_date).format("D/M/YYYY"),
            schedule_name: filteredList[0].xero_pay_calendar.Name,
            totalAmountSent: 0,
            totalFeeSent: 0,
            totalAmountUnSent: 0,
            totalFeeUnSent: 0,
            txn_count: 0,
          };
        }
        acc[fullname].totalAmountSent += parseFloat(
          get(row, "total_amount_sent", 0)
        );
        acc[fullname].totalFeeSent += parseFloat(get(row, "total_fee_sent", 0));
        acc[fullname].totalAmountUnSent += parseFloat(
          get(row, "total_amount_unsent", 0)
        );
        acc[fullname].totalFeeUnSent += parseFloat(
          get(row, "total_fee_unsent", 0)
        );
        acc[fullname].txn_count = transactionsByName[fullname];

        return acc;
      },
      {}
    );

    const groupedData = Object.values(aggregatedData);
    // Títulos principais
    worksheet.mergeCells("A1:F1");
    const titleCell = worksheet.getCell("A1");
    titleCell.value = "Astute Deductions";
    titleCell.alignment = { horizontal: "center", vertical: "middle" };
    titleCell.font = { bold: true, size: 14, color: { argb: "FF0000" } };

    // Informações da empresa e do período de pagamento
    worksheet.mergeCells("A2:F2");
    worksheet.getCell("A2").value =
      "Company: Hudson Global Resources (Aust) Pty Limited";
    worksheet.getCell("A3").value = "Pay Period:" + startDate + "-" + endDate;
    worksheet.getCell("A4").value = "Report Date:" + formattedDate;

    // Cabeçalhos da tabela
    const headers = [
      "No",
      "First Name",
      "Last Name",
      "Business Unit",
      "Astute ID",
      "Employee ID",
      "Capital",
      "Fee",
      "Txn Count",
      "Salary Cycle",
      "CashD Limit",
      "Limit",
      "Limit Reached",
      "Pay Cycle Period",
      "Pay Cycle Type",
      "Pay Cycle ID",
      "Date Time",
    ];
    worksheet.addRow(headers);

    // Estilizar cabeçalhos
    worksheet
      .getRow(5)
      .eachCell(
        (cell: {
          font: { bold: boolean; color: { argb: string } };
          fill: { type: string; pattern: string; fgColor: { argb: string } };
          alignment: { horizontal: string; vertical: string };
        }) => {
          cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
          cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "0073e6" },
          };

          cell.alignment = { horizontal: "center", vertical: "middle" };
        }
      );
    worksheet
      .getRow(5)
      .eachCell(
        { includeEmpty: false },
        (cell: {
          border: {
            top: { style: string; color: { argb: string } };
            left: { style: string; color: { argb: string } };
            bottom: { style: string; color: { argb: string } };
            right: { style: string; color: { argb: string } };
          };
        }) => {
          cell.border = {
            top: { style: "thin", color: { argb: "000000" } },
            left: { style: "thin", color: { argb: "000000" } },
            bottom: { style: "thin", color: { argb: "000000" } },
            right: { style: "thin", color: { argb: "000000" } },
          };
        }
      );

    groupedData.forEach((row: any, index) => {
      const newRow = worksheet.addRow([
        index + 1,
        row.first_name,
        row.last_name,
        row.business_unit_name,
        row.payroll_id,
        row.employee_id,
        row.totalAmountSent > 0
          ? row.totalAmountSent
          : row.totalAmountUnSent,
        row.totalFeeSent > 0 ? row.totalFeeSent : row.totalFeeUnSent,
        row.txn_count,
        "",
        "",
        "",
        "",
        row.pay_cycle_period,
        row.schedule_name,
        "1",
        formattedDate,
      ]);
      newRow.getCell(7).numFmt = `${cs}#,##0.00`;
      newRow.getCell(8).numFmt = `${cs}#,##0.00`;
    });

    // Ajustar largura das colunas
    const columnWidths = [
      5, 15, 15, 20, 10, 30, 10, 10, 10, 15, 15, 10, 10, 25, 20, 10, 15,
    ];
    columnWidths.forEach((width, index) => {
      worksheet.getColumn(index + 1).width = width;
    });

    const lastRow = worksheet.lastRow.number;
    worksheet.addRow([]);
    worksheet.getRow(lastRow + 1).getCell(7).numFmt = "[$$-409]#,##0.00";
    worksheet.getRow(lastRow + 1).getCell(7).value = {
      formula: `SUM(G6:G${lastRow})`,
    };
    worksheet.getRow(lastRow + 1).getCell(8).numFmt = "[$$-409]#,##0.00";
    worksheet.getRow(lastRow + 1).getCell(8).value = {
      formula: `SUM(H6:H${lastRow})`,
    };
    worksheet.getRow(lastRow + 1).getCell(9).value = {
      formula: `SUM(I6:I${lastRow})`,
    };

    worksheet
      .getRow(lastRow + 1)
      .eachCell(
        (
          cell: {
            fill: { type: string; pattern: string; fgColor: { argb: string } };
            font: { color: { argb: string } };
          },
          colNumber: number
        ) => {
          if ([7, 8, 9].includes(colNumber)) {
            cell.fill = {
              type: "pattern",
              pattern: "solid",
              fgColor: { argb: "92d050" },
            };
            cell.font = { color: { argb: "000000" } };
          }
        }
      );

    worksheet.addRow([]);
    worksheet.addRow([]);
    const capitalRow = worksheet.addRow([]);
    capitalRow.getCell(6).value = "CashD - Capital Amount";
    capitalRow.getCell(6).font = {
      color: { argb: "000000" },
    };
    capitalRow.getCell(7).value = { formula: `SUM(G6:G${lastRow})` };
    capitalRow.getCell(7).fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "4285f4" },
    };
    capitalRow.getCell(7).font = {
      color: { argb: "0073e6" },
    };
    capitalRow.getCell(7).font = { color: { argb: "FFFFFF" } };
    capitalRow.alignment = { horizontal: "left", vertical: "middle" };
    capitalRow.getCell(6).border = {
      top: { style: "thin", color: { argb: "000000" } },
      left: { style: "thin", color: { argb: "000000" } },
      bottom: { style: "thin", color: { argb: "000000" } },
      right: { style: "thin", color: { argb: "000000" } },
    };
    capitalRow.getCell(7).border = {
      top: { style: "thin", color: { argb: "000000" } },
      left: { style: "thin", color: { argb: "000000" } },
      bottom: { style: "thin", color: { argb: "000000" } },
      right: { style: "thin", color: { argb: "000000" } },
    };
    const serviceFeeRow = worksheet.addRow([]);
    serviceFeeRow.getCell(6).value = "CashD - Hudson Service Fee";
    serviceFeeRow.getCell(6).font = {
      color: { argb: "000000" },
    };
    serviceFeeRow.getCell(6).border = {
      top: { style: "thin", color: { argb: "000000" } },
      left: { style: "thin", color: { argb: "000000" } },
      bottom: { style: "thin", color: { argb: "000000" } },
      right: { style: "thin", color: { argb: "000000" } },
    };
    serviceFeeRow.getCell(7).value = { formula: `SUM(H6:H${lastRow})` };
    serviceFeeRow.getCell(7).fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "4285f4" },
    };
    serviceFeeRow.getCell(7).font = {
      color: { argb: "0073e6" },
    };
    serviceFeeRow.getCell(7).font = { color: { argb: "FFFFFF" } };
    serviceFeeRow.getCell(7).border = {
      top: { style: "thin", color: { argb: "000000" } },
      left: { style: "thin", color: { argb: "000000" } },
      bottom: { style: "thin", color: { argb: "000000" } },
      right: { style: "thin", color: { argb: "000000" } },
    };
    serviceFeeRow.alignment = { horizontal: "left", vertical: "middle" };

    const totalRepaymentRow = worksheet.addRow([]);

    totalRepaymentRow.getCell(6).value = "Total Repayment to CashD";
    totalRepaymentRow.getCell(6).font = {
      color: { argb: "000000" },
    };
    totalRepaymentRow.getCell(6).border = {
      top: { style: "thin", color: { argb: "000000" } },
      left: { style: "thin", color: { argb: "000000" } },
      bottom: { style: "thin", color: { argb: "000000" } },
      right: { style: "thin", color: { argb: "000000" } },
    };
    totalRepaymentRow.getCell(7).value = {
      formula: `${capitalRow.getCell(7).address}+${
        serviceFeeRow.getCell(7).address
        }`,
    };
    totalRepaymentRow.getCell(7).fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "92d050" },
    };
    totalRepaymentRow.getCell(7).font = {
      color: { argb: "FFFFFF" },
    };
    totalRepaymentRow.getCell(7).border = {
      top: { style: "thin", color: { argb: "000000" } },
      left: { style: "thin", color: { argb: "000000" } },
      bottom: { style: "thin", color: { argb: "000000" } },
      right: { style: "thin", color: { argb: "000000" } },
    };
    totalRepaymentRow.alignment = { horizontal: "left", vertical: "middle" };
    capitalRow.getCell(7).numFmt = "[$$-409]#,##0.00";
    serviceFeeRow.getCell(7).numFmt = "[$$-409]#,##0.00";
    totalRepaymentRow.getCell(7).numFmt = "[$$-409]#,##0.00";
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    // Crie o nome do arquivo com o formato desejado
    saveAs(blob, `${fileName}`);
  };

  //Export View deduction CSV
  const handleExportViewDeduction = () => {
    const filteredList = deductionTableList.filter(
      (item: any) => get(item, "_id", "") === viewPayPeriodId
    );
    const statusDeduction = get(
      filteredList,
      "[0].is_write_deductions_back_system",
      ""
    );
    const deductionStatus = statusDeduction ? "sent" : "unsent";
    authApi
      .get(
        `/v2/api/companies/${companyId}/DeductionFiles/${viewPayPeriodId}?page=${INITIAL_PAGE}&pageSize=${EXPORT_PAGE_SIZE}&deduction_type=${deductionStatus}`
      )
      .then(async (res: { data: { result: any } }) => {
        const finalData = res.data.result;
        authApi
          .get(
            `/v2/api/timesheets/getAllDeductions?page=0&pageSize=50000&company_id=${companyId}&pay_period_id=${viewPayPeriodId}&schedule_id=&business_unit_id=&startDate=`
          )
          .then(async (resTransactions) => {
            const transactions = resTransactions.data.data;
            switch (systemPayrollCode) {
              case "DEPUTY":
              case 'TIMESITE_PRO':
              case "MYOBEXO":
              case "RECKON":
              case "XERO":
                handleDeputyExportation(finalData, filteredList);
                break;
              case "MYOBADVANCED":
                handleMyobExportation(finalData, filteredList);
                break;
              case "ASTUTE":
                handleAstuteExportation(finalData, filteredList, transactions);
                break;
              default:
                if (
                  WEBHOOK_ENABLED_SYSTEMS.includes(systemPayrollCode) &&
                  !!filteredList[0]?.company_details?.webhook_enabled
                ) {
                  handleDeputyExportation(finalData, filteredList);
                  break;
                }
                handleMyobExportation(finalData, filteredList);
                break;
            }
            setLocalState({
              viewPayPeriodId: viewPayPeriodId,
              companyId: companyId,
            });
            handleClose();
          });
      })
      .catch((error) => {
        toast(get(error, "message", ""), { variant: "error" });
      });
  };

  const handleRunDeduction = () => {
    navigate(`/deductions/${companyId}/deductionFiles/${viewPayPeriodId}`, {
      state: { deductionTableList, deduction: deductionRecord },
    });

    handleClose();
  };
  const handleReverseDeduction = () => {
    setLocalState({ viewPayPeriodId: viewPayPeriodId, companyId: companyId });
    handleReverseDeductionDailog();
    handleClose();
  };

  const runSelectedDeductions = () => {
    dispatch(runDeduction(companyId, viewPayPeriodId));
    handleRunDeductionDailog();
  };
  const reverseSelectedDeductions = () => {
    dispatch(reverseDeduction(companyId, viewPayPeriodId));
    handleReverseDeductionDailog();
  };

  useEffect(() => {
    dispatch(resetDeductions());
    dispatch(
      fetchDeductionData(
        INITIAL_PAGE,
        ROWS_PER_PAGE,
        selectedCompanyId,
        fromDate,
        toDate,
        payPeriodId,
        scheduleId,
        businessUnitId,
        deductionStatus
      )
    ).finally(() => {
      setLocalState({
        page: INITIAL_PAGE,
        rowsPerPage: ROWS_PER_PAGE,
      });
    });
  }, [selectedCompanyId, payPeriod, schedule, businessUnit, deductionStatus]);

  useEffect(() => {
    if (fromDate && toDate) {
      dispatch(
        fetchDeductionData(
          INITIAL_PAGE,
          ROWS_PER_PAGE,
          selectedCompanyId,
          fromDate,
          toDate,
          payPeriodId,
          scheduleId,
          businessUnitId,
          deductionStatus
        )
      );
      setLocalState({
        page: INITIAL_PAGE,
        rowsPerPage: ROWS_PER_PAGE,
      });
    }
  }, [fromDate, toDate]);

  const getScheduleName = (data: any, systemCode: string) => {
    let scheduleName = { payPeriodName: "", payPeriodId: "" };
    const isWebhookEnabled = get(data, "company_details.webhook_enabled", false);
    const isWebhookSystem = WEBHOOK_ENABLED_SYSTEMS.includes(systemCode);

    if (
      ["TIMESITE_PRO", T.DEPUTY.toUpperCase()].includes(systemCode) ||
      (isWebhookEnabled && isWebhookSystem)
    ) {
      scheduleName.payPeriodName = get(data, "pay_period_origination.name", "");
      scheduleName.payPeriodId = get(data, "pay_period_origination._id", "");
    } else if(systemCode === "MYOBEXO"){
      scheduleName.payPeriodName = get(data, "pay_period_origination.display_name", "");
      scheduleName.payPeriodId = get(data, "pay_period_origination._id", "");
    }
    else if (systemCode === T.KEYPAY.toUpperCase()) {
      scheduleName.payPeriodName = get(data, "keypay_pay_schedule.name", "");
      scheduleName.payPeriodId = get(data, "keypay_pay_schedule._id", "");
    } else {
      scheduleName.payPeriodName = get(data, "xero_pay_calendar.Name", "");
      scheduleName.payPeriodId = get(data, "xero_pay_calendar._id", "");
    }
    return scheduleName;
  };

  const deductionFinalData: any = [];
  deductionTableList.map((data: any, index: number) => {
    const systemCode = get(data, "company_details.system_code.code", "");
    const scheduleData = getScheduleName(data, systemCode);

    deductionFinalData[index] = {
      srNo: index + 1,
      company_name: get(data, "company_details.company_name", ""),
      company_id: get(data, "company_details._id", ""),
      pay_periods: `${dateFormat(data.start_date)} To ${dateFormat(
        data.end_date
      )}`,
      deduction_status: data.is_write_deductions_back_system
        ? T.SENT
        : T.UNSENT,
      withdrawal_status: data.is_prevent_withdrawals ? T.LOCKED : T.OPEN,
      date_time: convertDateIntoFrontEndFormat(
        get(data, "write_deductions_date", "")
      ),
      amount: data.is_write_deductions_back_system
        ? data.total_deductions_sent
        : data.total_deductions_unsent,
      pay_period_id: get(data, "_id", ""),
      isPreventWithdrawals: get(data, "is_prevent_withdrawals", ""),
      payPeriodDetails: scheduleData,
      payGroupId: get(data, "payGroupId", "N/A"),
      systemCode: get(data, "company_details.system_code.code", ""),
      reckon_payrun_id: get(data, "reckon_payrun_id", ""),
      reckon_payrun_status: get(data, "reckon_payrun_status", ""),
      reckon_payrun_msg: get(data, "reckon_payrun_msg", ""),
      reckon_payrun_details: get(data, "reckon_payrun_details", null),
      xero_payrun_id: get(data, "xero_payrun_id", ""),
      xero_payrun_status: get(data, "xero_payrun_status", ""),
      xero_payrun_msg: get(data, "xero_payrun_msg", ""),
      xero_payrun_details: get(data, "xero_payrun_details", null),
    };
  });

  // Page change handler
  const handlePageChange = (newPage: number) => {
    setLocalState({ page: newPage });
    dispatch(
      fetchDeductionData(
        newPage,
        rowsPerPage,
        selectedCompanyId,
        fromDate,
        toDate,
        payPeriodId,
        scheduleId,
        businessUnitId,
        deductionStatus
      )
    );
    document.getElementsByClassName("MuiTableContainer-root")[0].scrollTop = 0;
  };

  // Rows per page change handler
  const handleRowsPerPageChange = (event: any) => {
    const { value } = event.target;
    setLocalState({ page: INITIAL_PAGE, rowsPerPage: value });
    dispatch(
      fetchDeductionData(
        INITIAL_PAGE,
        value,
        selectedCompanyId,
        fromDate,
        toDate,
        payPeriodId,
        scheduleId,
        businessUnitId,
        deductionStatus
      )
    );
  };

  //Date change handler
  const onHandleDateChange = (newValue: any, type: string) => {
    const validDate = newValue ? new Date(newValue) : null;

    setLocalState({
      [type]:
        validDate && isValid(validDate) ? getBEDateFormat(validDate) : null,
    });
  };

  //Autocomplete value change handler
  const onHandleAutoCompleteChange = (type: string, newValue: any) => {
    console.log("newValue",newValue)
    setLocalState({ [type]: newValue });
  };

  //Handler for clearing the filter
  const handleClearFilters = () => {
    setLocalState({
      fromDate: null,
      toDate: null,
      payPeriod: null,
      schedule: null,
      businessUnit: null,
      deductionStatus: null,
    });
    dispatch(
      fetchDeductionData(
        INITIAL_PAGE,
        rowsPerPage,
        selectedCompanyId,
        null,
        null,
        "",
        "",
        "",
        null
      )
    );
  };

  return (
    <>
      {loader && <Loader />}
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <MainCard
            title={
              <>
                <Grid container spacing={1} className="tableSearchBox">
                  <Grid item xs={12} sm={6} md={2}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DesktopDatePicker
                        label="From Date"
                        inputFormat="DD/MM/YYYY"
                        value={fromDate}
                        onChange={(date) =>
                          onHandleDateChange(date, "fromDate")
                        }
                        renderInput={(params) => <TextField {...params} />}
                      />
                    </LocalizationProvider>
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DesktopDatePicker
                        label="To Date"
                        inputFormat="DD/MM/YYYY"
                        value={toDate}
                        onChange={(date) => onHandleDateChange(date, "toDate")}
                        renderInput={(params) => <TextField {...params} />}
                      />
                    </LocalizationProvider>
                  </Grid>
                  {systemPayrollCode !== "MYOBADVANCED" &&
                    selectedCompanyId && (
                      <>
                        <Grid item xs={12} sm={6} md={3}>
                          <CashDAutocomplete
                            placeholder={T.SCHEDULE}
                            displayRemoveButton="none"
                            listDetails={scheduleList}
                            value={schedule}
                            getByLabelText={(option: any) =>
                              (systemPayrollCode === "MYOBEXO" && get(option, "display_name")) || get(option, "name", "") || get(option, "Name", "")
                            }
                            onHandleChange={(event: any, newValue: any) =>
                              onHandleAutoCompleteChange("schedule", newValue)
                            }
                          />
                        </Grid>

                        {selectedCompanyId &&
                          selectedCompanyName.includes(
                            capitalize(T.HUDSON)
                          ) && (
                            <Grid item xs={12} sm={6} md={2}>
                              <CashDAutocomplete
                                placeholder={T.BUSINESS_UNIT}
                                displayRemoveButton="none"
                                listDetails={businessUnitList}
                                value={businessUnit}
                                getByLabelText={(option: any) =>
                                  get(option, "name", "")
                                }
                                onHandleChange={(event: any, newValue: any) =>
                                  onHandleAutoCompleteChange(
                                    "businessUnit",
                                    newValue
                                  )
                                }
                              />
                            </Grid>
                          )}
                      </>
                    )}
                  <Grid item xs={12} sm={6} md={2}>
                    <CashDAutocomplete
                      placeholder={T.STATUS}
                      displayRemoveButton="none"
                      listDetails={DEDUCTION_STATUS_LIST}
                      value={deductionStatus}
                      getByLabelText={(option: any) => option}
                      onHandleChange={(event: any, newValue: any) =>
                        onHandleAutoCompleteChange("deductionStatus", newValue)
                      }
                    />
                  </Grid>
                  <Grid item xs={3} sm={3} md={1}>
                    <Box
                      sx={{
                        display: "flex",
                        "& h6": {
                          m: 1,
                        },
                        "& hr": {
                          mx: 0.2,
                          my: 0.1,
                        },
                      }}
                    >
                      <Divider
                        orientation="vertical"
                        variant="middle"
                        flexItem
                      />
                      <Typography
                        variant="h6"
                        className="reset"
                        onClick={handleClearFilters}
                      >
                        {T.RESET}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </>
            }
            content={false}
          >
            <ScrollX>
              <Deductions
                allTableRows={deductionFinalData}
                totalTableRowsCount={totalDeductionItems}
                totalPageCount={totalDeductionPages}
                page={page}
                rowsPerPage={rowsPerPage}
                onPageChange={handlePageChange}
                handleClick={handleClick}
                handleClose={handleClose}
                handleViewDeduction={handleViewDeduction}
                open={open}
                anchorEl={anchorEl}
                handleExportViewDeduction={handleExportViewDeduction}
                handleExportViewTransaction={handleExportViewTransaction}
                deductionViewList={viewDeductionRows}
                onRowsPerPageChange={handleRowsPerPageChange}
                handleRunDeduction={handleRunDeduction}
                handleReverseDeduction={handleReverseDeduction}
              />
            </ScrollX>
          </MainCard>
        </Grid>
      </Grid>
      <RunDeduction
        openRunDeductionDialog={openRunDeductionDialog}
        handleRunDeductionDailog={handleRunDeductionDailog}
        runSelectedDeductions={runSelectedDeductions}
      />
      <ReverseDeduction
        openReverseDeductionDialog={openReverseDeductionDialog}
        handleReverseDeductionDailog={handleReverseDeductionDailog}
        reverseSelectedDeductions={reverseSelectedDeductions}
      />
    </>
  );
};

export default DeductionPage;
